import { supabase } from "./supabaseClient.js";
import { uploadFileToMinIO, deleteFileFromMinIO } from "./minioClient.js";

const BUCKET_NAME = import.meta.env.VITE_MINIO_BUCKET_FILES;

function getKeyFromUrl(url) {
  if (!url) return null;
  // Url format: endpoint/bucket/filename
  // We split by bucket name to get the key/filename
  const parts = url.split(`/${BUCKET_NAME}/`);
  return parts.length > 1 ? parts[1] : null;
}

export async function listSuppliers() {
  const { data, error } = await supabase
    .from("suppliers")
    .select("*")
    .order("name", { ascending: true });
  
  if (error) {
    console.error("Error fetching suppliers:", error);
    return [];
  }
  return data || [];
}

export async function createSupplier(supplier, imageFile = null) {
  // supplier: { name, phone, email, address }
  let imageUrl = null;
  
  if (imageFile) {
    imageUrl = await uploadFileToMinIO(imageFile, "suppliers/");
  }

  const { data, error } = await supabase
    .from("suppliers")
    .insert({ ...supplier, image_url: imageUrl })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateSupplier(id, updates, imageFile = null) {
  // updates can contain normal fields. 
  // If imageFile is provided, we replace the image.
  // If updates.image_url is explicitly set to null (meaning delete), we handle that too.
  
  let newImageUrl = undefined;

  // 1. If new file, upload it
  if (imageFile) {
    newImageUrl = await uploadFileToMinIO(imageFile, "suppliers/");
  }

  // 2. Determine if we need to delete old image (if replacing or removing)
  if (imageFile || updates.image_url === null) {
    // We need current data to know the old URL
    const { data: current } = await supabase
      .from("suppliers")
      .select("image_url")
      .eq("id_suppliers", id)
      .single();
      
    if (current && current.image_url) {
      const key = getKeyFromUrl(current.image_url);
      if (key) {
        await deleteFileFromMinIO(key);
      }
    }
  }

  // 3. Prepare payload
  const payload = { ...updates };
  if (newImageUrl) {
    payload.image_url = newImageUrl;
  } else if (updates.image_url === null) {
    // Explicit removal
    payload.image_url = null;
  }

  const { data, error } = await supabase
    .from("suppliers")
    .update(payload)
    .eq("id_suppliers", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteSupplier(id) {
  // 1. Get current to check for image
  const { data: current } = await supabase
    .from("suppliers")
    .select("image_url")
    .eq("id_suppliers", id)
    .single();

  // 2. Delete image from MinIO if exists
  if (current && current.image_url) {
    const key = getKeyFromUrl(current.image_url);
    if (key) {
      await deleteFileFromMinIO(key);
    }
  }

  // 3. Delete record
  const { error } = await supabase
    .from("suppliers")
    .delete()
    .eq("id_suppliers", id);

  if (error) throw error;
  return true;
}
